/**
 * KeepAwakePort — keep the screen awake DURING ACTIVE RECORDING ONLY (D-008).
 *
 * This is the ports layer (ARCHITECTURE §3, CLAUDE.md §8). Preventing the
 * screen from sleeping is a DEVICE capability, so it lives beside
 * `RecorderPort` — and like the recorder, the web implementation IS the
 * Capacitor implementation: the Screen Wake Lock API is a standard web runtime
 * capability available inside the Chromium WebView Capacitor uses on Android
 * (and WKWebView from iOS 16.4). **No native plugin, no new dependency** —
 * S-03-04's authorization is explicit that a new dependency is a stop-and-ask,
 * and none is needed.
 *
 * THE CONTRACT IS DELIBERATELY HONEST ABOUT FAILURE (PT §20):
 *
 *   * `acquire()` resolves `true` only when a wake lock is genuinely HELD. An
 *     unsupported runtime, a refused request (battery saver, hidden page) or a
 *     rejecting API resolves `false`. It NEVER throws and it never fabricates
 *     a held state — keep-awake is a reliability aid, and a recorder that
 *     crashed because a convenience failed would have the priorities backwards.
 *   * The OS may release the lock on its own (screen off, tab hidden). That is
 *     accepted as-is: `isHeld()` reads the sentinel's own `released` flag, so
 *     it cannot claim a lock the system already took back. Nothing re-acquires
 *     behind the user's back — if the screen slept anyway, pretending
 *     otherwise helps nobody.
 *   * `release()` is safe to call at any time, held or not, and settles even
 *     if the underlying sentinel rejects. Callers release on EVERY recording
 *     exit path and must never need to wonder whether it was safe.
 *
 * RACE SAFETY: `release()` can arrive while `acquire()` is still awaiting the
 * runtime (a recording stopped in the same breath it started). A generation
 * counter resolves the ownership: `release()` bumps it, and an acquisition
 * that comes back to find its generation stale releases the sentinel it just
 * received instead of holding a lock nobody owns.
 */

/** The slice of a `WakeLockSentinel` this port relies on. Structural, so tests
 * can supply a plain object and no `lib.dom` version is load-bearing. */
export interface WakeLockSentinelLike {
  readonly released: boolean;
  release(): Promise<void>;
}

/** The slice of `navigator.wakeLock` this port relies on. */
export interface WakeLockLike {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
}

export interface KeepAwakePort {
  /** Human-readable implementation name. Diagnostics only, never product UI. */
  readonly name: string;
  /** Whether this runtime has the API at all. Says nothing about permission. */
  isSupported(): boolean;
  /**
   * Request a screen wake lock. Resolves `true` only when the lock is
   * genuinely held; `false` on unsupported runtimes and refused or failed
   * requests. Never rejects.
   */
  acquire(): Promise<boolean>;
  /** Release the lock if held. Safe to call at any time. Never rejects. */
  release(): Promise<void>;
  /** Whether a lock is held RIGHT NOW, honest about system-side release. */
  isHeld(): boolean;
}

/** Reads `navigator.wakeLock` without assuming the lib version declares it. */
function readNavigatorWakeLock(): WakeLockLike | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const wakeLock = (navigator as Navigator & { wakeLock?: WakeLockLike }).wakeLock;
  return typeof wakeLock?.request === 'function' ? wakeLock : undefined;
}

/**
 * The Screen Wake Lock implementation.
 *
 * `wakeLock` is injectable so every path — held, refused, rejecting, released
 * mid-flight, absent entirely — is provable without a real browser lock.
 */
export function createWebKeepAwake(
  options: {
    /** Overridable for tests. Defaults to reading `navigator.wakeLock`. */
    wakeLock?: () => WakeLockLike | undefined;
  } = {},
): KeepAwakePort {
  const getWakeLock = options.wakeLock ?? readNavigatorWakeLock;

  /**
   * Ownership generation. `release()` bumps it; an `acquire()` whose awaited
   * sentinel arrives under a stale generation releases that sentinel rather
   * than storing it — otherwise a stop that raced a start would leave a wake
   * lock held with no recording behind it.
   */
  let generation = 0;
  let sentinel: WakeLockSentinelLike | null = null;

  return {
    name: 'web:ScreenWakeLock',

    isSupported() {
      return getWakeLock() !== undefined;
    },

    async acquire() {
      // Already genuinely held — nothing to do. A sentinel the system has
      // released does NOT count; that path falls through to a fresh request.
      if (sentinel !== null && !sentinel.released) return true;
      sentinel = null;

      const wakeLock = getWakeLock();
      if (wakeLock === undefined) return false;

      const gen = ++generation;
      let acquired: WakeLockSentinelLike;
      try {
        acquired = await wakeLock.request('screen');
      } catch {
        // Refused (battery saver, hidden document) or failed. The honest
        // answer is "not held", and recording proceeds without it.
        return false;
      }

      if (gen !== generation) {
        // A release() overtook this acquisition. Holding the sentinel now
        // would keep the screen awake with nothing recording — let it go.
        try {
          await acquired.release();
        } catch {
          // Already released. The outcome is the same either way.
        }
        return false;
      }

      sentinel = acquired;
      return true;
    },

    async release() {
      generation += 1; // Orphans any acquisition still in flight.
      const held = sentinel;
      sentinel = null;
      if (held === null) return;
      try {
        await held.release();
      } catch {
        // Already released — by the system or by a double release. Fine.
      }
    },

    isHeld() {
      return sentinel !== null && !sentinel.released;
    },
  };
}

/**
 * Chosen once, like every other port (ARCHITECTURE §3).
 *
 * One implementation, deliberately: the Screen Wake Lock API is the same
 * standard capability in the browser and inside both Capacitor WebViews, so a
 * separate native implementation would be a dependency bought for nothing. If
 * real-device acceptance ever shows the WebView lacks it, the native
 * implementation is added HERE and Capture never learns about it.
 */
export function selectKeepAwake(): KeepAwakePort {
  return createWebKeepAwake();
}
